import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

export default function useTutorialLightbox() {
  const [medias, setMedias] =
    useState([]);

  const [
    currentIndex,
    setCurrentIndex,
  ] = useState(0);

  const isOpen =
    medias.length > 0;

  const currentMedia =
    medias[currentIndex] || null;

  const hasPrevious =
    currentIndex > 0;

  const hasNext =
    currentIndex <
    medias.length - 1;

  const openLightbox =
    useCallback(
      (
        availableMedias,
        selectedIndex = 0
      ) => {
        const imageMedias = (
          availableMedias || []
        ).filter(
          (media) =>
            media.type === "image"
        );

        if (!imageMedias.length) {
          return;
        }

        const safeIndex = Math.min(
          Math.max(
            Number(selectedIndex) || 0,
            0
          ),
          imageMedias.length - 1
        );

        setMedias(imageMedias);
        setCurrentIndex(safeIndex);
      },
      []
    );

  const closeLightbox =
    useCallback(() => {
      setMedias([]);
      setCurrentIndex(0);
    }, []);

  const showPrevious =
    useCallback(() => {
      setCurrentIndex((current) =>
        Math.max(current - 1, 0)
      );
    }, []);

  const showNext =
    useCallback(() => {
      setCurrentIndex((current) =>
        Math.min(
          current + 1,
          medias.length - 1
        )
      );
    }, [medias.length]);

  /*
   * Verrouillage du défilement pendant
   * l'ouverture de la LightBox.
   *
   * Cet effet doit exister à un seul
   * endroit dans l'application.
   */
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow =
      document.body.style.overflow;

    const previousPaddingRight =
      document.body.style.paddingRight;

    /*
     * Évite un léger déplacement de la
     * page lorsque l'ascenseur disparaît.
     */
    const scrollbarWidth =
      window.innerWidth -
      document.documentElement
        .clientWidth;

    if (scrollbarWidth > 0) {
      document.body.style.paddingRight =
        `${scrollbarWidth}px`;
    }

    document.body.style.overflow =
      "hidden";

    return () => {
      document.body.style.overflow =
        previousOverflow;

      document.body.style.paddingRight =
        previousPaddingRight;
    };
  }, [isOpen]);

  /*
   * Navigation clavier.
   */
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (
      event
    ) => {
      if (event.key === "Escape") {
        closeLightbox();
        return;
      }

      if (
        event.key === "ArrowLeft" &&
        hasPrevious
      ) {
        showPrevious();
      }

      if (
        event.key === "ArrowRight" &&
        hasNext
      ) {
        showNext();
      }
    };

    window.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      window.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [
    isOpen,
    hasPrevious,
    hasNext,
    closeLightbox,
    showPrevious,
    showNext,
  ]);

  return useMemo(
    () => ({
      medias,
      currentIndex,
      currentMedia,

      isOpen,
      hasPrevious,
      hasNext,

      openLightbox,
      closeLightbox,
      showPrevious,
      showNext,
    }),
    [
      medias,
      currentIndex,
      currentMedia,
      isOpen,
      hasPrevious,
      hasNext,
      openLightbox,
      closeLightbox,
      showPrevious,
      showNext,
    ]
  );
}